import React, { createContext, useContext, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../../../hooks/redux';
import LiveGuidelineProductTagsModal from '../../../../../../components/liveGuidelineProductTagModal/LiveGuidelineProductTagsModal';
import {
    deleteLiveGuidelineProductTagAction,
    getLiveGuidelineProductTagsByLiveGuidelineProductIdAction,
} from '../../../../../../redux/actions/liveGuidelineProductTagActions';
import { alertService } from '../../../../../../services';

type LiveGuidelineProductTagsContextType = {
    open: (liveGuidelineProductId: number) => void;
    close: () => void;
    deleteTag: (liveGuidelineProductTagId: number) => void;
};

export const LiveGuidelineProductTagsContext = createContext<LiveGuidelineProductTagsContextType>({
    open: () => {},
    close: () => {},
    deleteTag: (_liveGuidelineProductTagId: number) => {},
});

export const useLiveGuidelineProductTags = () => useContext(LiveGuidelineProductTagsContext);

export const LiveGuidelineProductTagsProvider: React.FC<{ children: any; onSaved?: () => void }> = ({
    children,
    onSaved,
}) => {
    const dispatch = useAppDispatch();

    const [visible, setVisible] = useState(false);
    const [liveGuidelineProductId, setLiveGuidelineProductId] = useState<number | null>(null);

    const open = (id: number) => {
        setLiveGuidelineProductId(id);
        setVisible(true);
        dispatch(getLiveGuidelineProductTagsByLiveGuidelineProductIdAction(id));
    };

    const close = () => {
        setVisible(false);
        setLiveGuidelineProductId(null);
    };

    const handleDeleteLiveGuidelineProductTag = async (liveGuidelineProductTagId: number) => {
        if (!liveGuidelineProductTagId) {
            return;
        }
        try {
            await dispatch(deleteLiveGuidelineProductTagAction(liveGuidelineProductTagId));
            alertService.success('Tag removed');
        } catch (e) {
            console.error(e);
            alertService.error('Failed to remove tag');
        }
    };

    const contextValue = useMemo(
        () => ({
            open,
            close,
            deleteTag: handleDeleteLiveGuidelineProductTag,
        }),
        [open, close, handleDeleteLiveGuidelineProductTag],
    );

    return (
        <LiveGuidelineProductTagsContext.Provider value={contextValue}>
            {children}
            <LiveGuidelineProductTagsModal
                visible={visible}
                liveGuidelineProductId={liveGuidelineProductId}
                onClose={close}
                onSaved={onSaved}
            />
        </LiveGuidelineProductTagsContext.Provider>
    );
};

export default LiveGuidelineProductTagsContext;
